import React, { Component } from 'react';

import connector from 'Utils/connector';
import connectAmp, {
  ConnectAmpProps,
} from 'Webapp/shared/app/connectors/connectAmp';
import connectFeatureFlags, {
  ConnectFeatureFlagsProps,
} from 'Webapp/shared/app/connectors/connectFeatureFlags';
import connectAdTargeting, {
  ConnectAdTargetingProps,
} from 'Webapp/shared/app/connectors/connectAdTargeting';

import AdSlot from './ad-slot';
import {
  AD_UNIT_PATHS,
  MEDIUM_RECTANGLE_SIZES,
  isInfeedAdEnabled,
  isBrandSafetyTargetingEnabled,
} from './ads-config';
import { setPageTargeting } from './gpt';
import { brandSafetyTargeting } from './brand-safety';
import { renderNativeAdCard } from './native-ad-card';

type InfeedAdCardProps = ConnectAmpProps &
  ConnectFeatureFlagsProps &
  ConnectAdTargetingProps & {
    /**
     * The story tiles directly above and below this card, in display order,
     * for the brand-safety key-values (see ./brand-safety.ts).
     */
    adjacentItems?: Array<Flipboard.Item | undefined>;
  };

/**
 * Ad card inserted into section story grids as an EXTRA tile (never replacing
 * content) — see the injection in views/section/item-list.tsx.
 *
 * Serves SELF-RENDERED GAM NATIVE ads: a ['fluid'] GPT request (native format)
 * whose creative fields we render into a Flipboard promoted-post tile (image /
 * headline / advertiser), rather than a boxed 300x250 display banner. The
 * "Sponsored"/"Advertisement" label is required disclosure (FTC/IAB) since the
 * tile is styled like editorial content. On no-fill nothing renders and the
 * grid tile collapses. flint header bidding and lazy loading are preserved by
 * reusing AdSlot's request lifecycle.
 */
class InfeedAdCard extends Component<InfeedAdCardProps> {
  componentDidMount() {
    this.applyPageTargeting();
  }

  shouldRender = () => {
    const { isAmp, featureFlags } = this.props;
    return !isAmp && isInfeedAdEnabled(featureFlags);
  };

  applyPageTargeting = () => {
    if (!this.shouldRender()) {
      return;
    }
    const { adPageType, adTopicSlug } = this.props;
    setPageTargeting({
      page_type: adPageType,
      topic_slug: adTopicSlug,
    });
  };

  render() {
    if (!this.shouldRender()) {
      return null;
    }
    const { adPageType, adTopicSlug, adjacentItems, featureFlags } = this.props;
    const targeting: Record<string, string | Array<string>> = {};
    if (adPageType) {
      targeting.page_type = adPageType;
    }
    if (adTopicSlug) {
      targeting.topic_slug = adTopicSlug;
    }
    if (isBrandSafetyTargetingEnabled(featureFlags) && adjacentItems) {
      Object.assign(targeting, brandSafetyTargeting(adjacentItems));
    }
    // No boxed wrapper: the native card IS the tile, and an unfilled slot
    // renders nothing so the grid collapses the tile (no empty box).
    return (
      <AdSlot
        name="infeed"
        placementFlag="WEB_ADS_INFEED"
        adUnitPath={AD_UNIT_PATHS.infeed}
        // Native (fluid) request. sizes are ignored for the GPT request but
        // still give flint bid-size context.
        native
        renderNative={renderNativeAdCard}
        sizes={MEDIUM_RECTANGLE_SIZES}
        // Story-grid tile styled like editorial content — FTC/IAB native-ad
        // disclosure applies (unlike the visually-distinct banner slots).
        showLabel
        lazy
        targeting={targeting}
      />
    );
  }
}

export default connector<InfeedAdCardProps>(
  connectAmp,
  connectFeatureFlags,
  connectAdTargeting,
)(InfeedAdCard);
